import { motion } from "framer-motion";
import { Warehouse, Sun, Droplets, Wrench, Eye } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const infrastructure = [
  { icon: Warehouse, title: "Pot Nursery Area", description: "Dedicated space for seedling cultivation" },
  { icon: Sun, title: "Shade Net Facility", description: "Protected environment for young plants" },
  { icon: Droplets, title: "Irrigation System", description: "Adequate water source and supply" },
  { icon: Wrench, title: "Tools & Equipment", description: "Modern nursery equipment" },
  { icon: Eye, title: "Regular Monitoring", description: "Continuous plant care and observation" },
];

export default function InfrastructureSection() {
  return (
    <section
      id="infrastructure"
      className="py-20 md:py-32 bg-[#F9F8F4]"
      data-testid="infrastructure-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div {...fadeUp} className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-sm font-medium tracking-widest uppercase text-[#E07A5F] mb-4 block">
            Our Facilities
          </span>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#1A1C18] mb-6 tracking-tight"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            Nursery Infrastructure
          </h2>
          <p className="text-[#4A4F45] text-lg leading-relaxed">
            State-of-the-art facilities designed for optimal seedling growth and quality assurance.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 auto-rows-[280px]">
          {/* Large Image - Greenhouse */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="md:col-span-2 md:row-span-2 bento-item img-zoom rounded-3xl overflow-hidden relative"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_dxt-coconuts/artifacts/isv2t3xu_IMG20260202172246.jpg"
              alt="Hybrid Coconut Seedlings nursery at Kishan Coconut Nursery"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <h3
                className="text-2xl font-semibold text-white mb-2"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Modern Greenhouse
              </h3>
              <p className="text-white/80 text-sm">
                Climate-controlled environment for optimal growth
              </p>
            </div>
          </motion.div>

          {/* Stat Card 1 */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="bg-[#2F5233] p-8 rounded-3xl flex flex-col justify-between bento-item"
          >
            <Warehouse className="w-10 h-10 text-white/80" />
            <div>
              <p
                className="text-4xl font-bold text-white mb-2"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Pot
              </p>
              <p className="text-white/70">Nursery Area</p>
            </div>
          </motion.div>

          {/* Stat Card 2 */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.3 }}
            className="bg-[#E07A5F] p-8 rounded-3xl flex flex-col justify-between bento-item"
          >
            <Sun className="w-10 h-10 text-white/80" />
            <div>
              <p
                className="text-4xl font-bold text-white mb-2"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Shade
              </p>
              <p className="text-white/70">Net Facility</p>
            </div>
          </motion.div>

          {/* Image - Field */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.4 }}
            className="md:col-span-2 bento-item img-zoom rounded-3xl overflow-hidden relative"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_dxt-coconuts/artifacts/9zzfxi0s_IMG20260202172829.jpg"
              alt="Coconut seedlings at Kishan Coconut Nursery"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <p className="text-white font-semibold">Seedling Cultivation Area</p>
            </div>
          </motion.div>
        </div>

        {/* Features List */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.5 }}
          className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-4"
        >
          {infrastructure.map((item, index) => (
            <div
              key={item.title}
              className="bg-white p-6 rounded-2xl border border-[#E5E2D9] text-center feature-card"
            >
              <item.icon className="w-8 h-8 text-[#2F5233] mx-auto mb-3" />
              <p className="font-medium text-[#1A1C18] text-sm">{item.title}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
